import { asyncPipe, pipe, tap } from '@helo/utils'
import { StateCreator } from 'zustand'

import { PersistenceSlice } from '../persistence/persistenceSlice'
import { QuerySlice } from '../queryStore/querySlice'
import { RouterSlice } from '../router/routerSlice'
import { ViewSliceActions } from './viewSlice.actions'
import { DEFAULT_VIEW_STORE_STATE, persistView } from './viewSlice.parts'
import {
  SavedView,
  ViewQueryState,
  ViewState,
  WidgetState,
} from './viewSlice.types'

export type ViewStore = {
  baseViewState: ViewState
  contentViewState: ViewState
  savedView: SavedView | null
  savedViewId: string | undefined
  viewUrlState: Partial<ViewState>
  viewId: string | null
  // queryStates: ViewQueryState[]
  // widgetStates: WidgetState[]
  viewState: ViewState
  /**
   * Whether the current view state matches the base state.
   */
  isBaseState: boolean
  // initializeView: (options?: Partial<ViewStore>) => void
  getQueryState: (queryId: string) => ViewQueryState | undefined
  getWidgetState: (widgetId: string) => WidgetState | undefined
  setWidgetState: (widgetState: WidgetState, persist?: boolean) => ViewStore
  setQueryState: (queryState: ViewQueryState, persist?: boolean) => ViewStore
  setWidgetBaseState: (widgetState: WidgetState, persist?: boolean) => ViewStore
  setQueryBaseState: (
    queryState: ViewQueryState,
    persist?: boolean,
  ) => ViewStore
  removeQueryState: (queryId: string, persist?: boolean) => void
  removeWidgetState: (widgetId: string, persist?: boolean) => void
  persistView: () => void
  createSavedView: (
    savedView: Pick<Partial<SavedView>, 'name' | 'description' | 'tableConfig'>,
  ) => Promise<ViewStore>
  updateSavedView: (
    savedView: Pick<Partial<SavedView>, 'name' | 'description' | 'tableConfig'>,
  ) => Promise<ViewStore>
}

export type ViewSlice = {
  viewStore: ViewStore
}

export const viewSlice: StateCreator<
  ViewSlice & PersistenceSlice & QuerySlice & RouterSlice,
  [],
  [],
  ViewSlice
> = (set, get, storeApi) => ({
  viewStore: {
    ...DEFAULT_VIEW_STORE_STATE,
    getQueryState: (queryId: string) =>
      pipe(get(), ViewSliceActions.getQueryState(queryId)),
    getWidgetState: (widgetId: string) =>
      pipe(get(), ViewSliceActions.getWidgetState(widgetId)),
    // // TODO: think about persistence and if we can store it in the widget state instead.
    setWidgetState: (widgetState: WidgetState, persist: boolean = false) =>
      pipe(
        get(),
        ViewSliceActions.setWidgetState(widgetState, persist),
        tap(store => set(store)),
      ),
    setWidgetBaseState: (baseState: WidgetState) =>
      pipe(
        get(),
        ViewSliceActions.setWidgetBaseState(baseState),
        tap(store => set(store)),
      ),
    // TODO: think about persistence and if we can store it in the query state instead.
    setQueryState: (queryState: ViewQueryState, persist: boolean = true) =>
      pipe(
        get(),
        ViewSliceActions.setQueryState(queryState),
        tap(store => set(store)),
      ),

    setQueryBaseState: (baseState: ViewQueryState) =>
      pipe(
        get(),
        ViewSliceActions.setQueryBaseState(baseState),
        tap(store => set(store)),
      ),
    removeQueryState: (queryId: string, persist: boolean = true) =>
      pipe(
        get(),
        ViewSliceActions.removeQueryState(queryId),
        tap(store => set(store)),
      ),
    removeWidgetState: (widgetId: string, persist: boolean = true) =>
      pipe(
        get(),
        ViewSliceActions.removeWidgetState(widgetId, persist),
        tap(store => set(store)),
      ),
    createSavedView: savedView =>
      asyncPipe(
        get(),
        ViewSliceActions.createSavedView(savedView, storeApi),
        tap(store => set(store)),
      ),
    updateSavedView: savedView =>
      asyncPipe(
        get(),
        ViewSliceActions.updateSavedView(savedView, storeApi),
        tap(store => set(store)),
      ),
    persistView: () => {
      pipe(
        get(),
        persistView(),
        tap(store => set(store)),
      )
    },
  },
})
